<template>
  <div class="config-item" :class="[{ active: isActive }, variant === 'archive' ? 'config-item-archive' : '']" @click="handleClick">
    <div v-if="isActive && variant !== 'archive'" class="active-rail"></div>

    <div class="item-content">
      <input v-if="isRenaming" ref="inputRef" class="config-input" type="text" v-model="renameValue" :placeholder="t('config_placeholder')" @blur="finishRename" @click.stop />
      <div v-else class="item-copy">
        <span class="item-name" :title="item.Name">{{ item.Name }}</span>
        <span class="item-meta" :class="{ 'item-meta-unsynced': variant === 'archive' }">{{ metaText }}</span>
        <div v-if="variant !== 'archive'" class="item-progress" aria-hidden="true">
          <div class="item-progress-bar"></div>
        </div>
      </div>
    </div>

    <div class="item-actions" v-if="!isRenaming">
      <!-- 快捷键按钮：仅 active / normal 变体 -->
      <div
        v-if="isFeatureSupported && variant !== 'archive'"
        class="action-btn shortcut"
        @click.stop="emit('set-shortcut', index)"
        :title="shortcutTitle"
        :class="{
          'has-shortcut': item.Shortcut && item.Shortcut.length > 0,
          error: item.ShortcutRet == false && item.Shortcut != null && item.Shortcut.length > 0,
          success: item.ShortcutRet == true && item.Shortcut != null && item.Shortcut.length > 0,
        }"
      >
        <SvgAsset :content="configShortcutSvg" />
      </div>
      <!-- 设备功能按钮：仅 active 变体 -->
      <div v-if="variant === 'active'" class="action-btn" @click.stop="emit('go-device-function')" :title="t('config_set_switch_key')">
        <SvgAsset :content="onboardSwitchSvg" />
      </div>
      <!-- 重命名按钮：所有变体 -->
      <div class="action-btn" @click.stop="startRename" :title="t('config_rename')">
        <SvgAsset :content="configEditSvg" />
      </div>
      <!-- 删除按钮：normal（非活跃）+ archive 变体 -->
      <div v-if="canDelete" class="action-btn delete" @click.stop="emit('delete', index)" :title="t('config_delete')">
        <SvgAsset :content="configDeleteSvg" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, nextTick } from 'vue'
import type { ConfigList } from '@/service/driver/config_repository'
import SvgAsset from '../common/SvgAsset.vue'
import configDeleteSvg from '@/assets/inline-svg/config-delete.svg?raw'
import configEditSvg from '@/assets/inline-svg/config-edit.svg?raw'
import configShortcutSvg from '@/assets/inline-svg/config-shortcut.svg?raw'
import onboardSwitchSvg from '@/assets/inline-svg/onboard-switch.svg?raw'
import i18n from '@/languages/i18n'
import { getStringAfterDash } from '@/tool/utility'
import { kbVKTexture } from '@/define/vkcode'

const { t } = i18n.global

const props = defineProps<{
  item: ConfigList
  /** 该配置在 configList.value 中的原始索引 */
  index: number
  isActive: boolean
  variant: 'active' | 'normal' | 'archive'
  isFeatureSupported: boolean
  /** 板载槽位编号，仅 active 变体需要传入 */
  onboardSlot?: number | null
}>()

const emit = defineEmits<{
  click: [index: number]
  'rename-complete': [index: number, newName: string]
  delete: [index: number]
  'set-shortcut': [index: number]
  'go-device-function': []
}>()

// ─── 内部重命名状态 ───
const isRenaming = ref(false)
const renameValue = ref('')
const inputRef = ref<HTMLInputElement | null>(null)

const startRename = () => {
  isRenaming.value = true
  renameValue.value = props.item.Name
  nextTick(() => {
    inputRef.value?.focus()
  })
}

const finishRename = () => {
  if (!isRenaming.value) return
  isRenaming.value = false
  const trimmed = renameValue.value.trim()
  if (trimmed.length === 0 || renameValue.value === props.item.Name) return
  emit('rename-complete', props.index, renameValue.value)
}

const handleClick = () => {
  if (!isRenaming.value) {
    emit('click', props.index)
  }
}

const metaText = computed(() => {
  if (props.variant === 'archive') return t('not_synced')
  if (props.variant === 'active') return `${t('onboard_config_slot_prefix')} ${(props.onboardSlot ?? 0) + 1}`
  return `#${String(props.index + 1).padStart(2, '0')}`
})

const canDelete = computed(() => {
  return props.variant === 'archive' || (props.variant === 'normal' && !props.isActive)
})

const shortcutTitle = computed(() => {
  if (props.item.ShortcutRet == false && props.item.Shortcut != null && props.item.Shortcut.length > 0) {
    return t('hot_key_registered')
  }
  const vk = props.item.Shortcut
  if (!vk) return ''
  return vk
    .filter((e) => e > 0)
    .map((v) => getStringAfterDash(kbVKTexture[v]))
    .join('+')
})
</script>

<style lang="less" scoped>
.config-item {
  padding: 18px 18px 16px;
  background: rgba(255, 255, 255, 0.45);
  /* backdrop-filter 已移除：config-item 在已模糊的 .right-sidebar 容器内，
     嵌套 backdrop-filter 会额外创建合成层，当弹窗冻结策略 (mgx-modal-open) 移除
     父级模糊后，子级白色边框会裸露。父容器的 blur(25px) 已足够 */
  border-radius: var(--radius-m);
  border: 1px solid rgba(255, 255, 255, 0.12);
  cursor: pointer;
  transition: all 0.4s cubic-bezier(0.23, 1, 0.32, 1);
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: relative;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.03);
  flex-shrink: 0;

  .active-rail {
    position: absolute;
    left: 0;
    top: 50%;
    width: 5px;
    height: 42px;
    border-radius: 0 var(--radius-pill) var(--radius-pill) 0;
    background: var(--brand-color-pure);
    box-shadow: 2px 0 10px rgba(var(--color-brand-1-rgb), 0.25);
    transform: translateY(-50%) scaleY(0.78);
    transform-origin: center;
    transition:
      transform 0.45s cubic-bezier(0.23, 1, 0.32, 1),
      box-shadow 0.45s cubic-bezier(0.23, 1, 0.32, 1);
  }

  .item-content {
    flex: 1;
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
    z-index: 1;

    .item-copy {
      flex: 1;
      min-width: 0;
      display: flex;
      flex-direction: column;
      gap: 5px;
    }

    .item-name {
      font-family: var(--font-main);
      font-size: 11px;
      color: var(--surface-text-primary);
      font-weight: 900;
      letter-spacing: 0.08em;
      line-height: 1.15;
      text-transform: uppercase;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      transition: var(--transition-base);
    }

    .item-meta {
      font-size: 9px;
      font-weight: 800;
      letter-spacing: 0.16em;
      line-height: 1;
      color: var(--surface-text-secondary);
      text-transform: uppercase;
      transition: var(--transition-base);
    }

    .item-meta-unsynced {
      color: var(--color-text-hint-rgb);
      font-style: italic;
    }

    .item-progress {
      position: relative;
      width: 100%;
      height: 4px;
      margin-top: 4px;
      background: rgba(0, 0, 0, 0.05);
      border-radius: var(--radius-pill);
      overflow: hidden;

      .item-progress-bar {
        width: 0;
        height: 100%;
        border-radius: inherit;
        background: var(--brand-color-pure);
        opacity: 0.9;
        transition:
          width 0.7s cubic-bezier(0.23, 1, 0.32, 1),
          opacity 0.4s cubic-bezier(0.23, 1, 0.32, 1),
          box-shadow 0.4s cubic-bezier(0.23, 1, 0.32, 1);
        box-shadow: 0 0 0 rgba(var(--color-brand-2-rgb), 0);
      }
    }

    .config-input {
      flex: 1;
      background: transparent;
      border: none;
      border-bottom: 2px solid var(--brand-color-pure);
      padding: 2px 0;
      font-size: 13px;
      font-family: var(--font-main);
      font-weight: 700;
      color: var(--brand-color-pure);
      outline: none;

      &::placeholder {
        color: rgba(var(--color-brand-1-rgb), 0.4);
      }
    }
  }

  .item-actions {
    position: absolute;
    right: 12px;
    top: 12px;
    transform: translateX(8px);
    display: flex;
    gap: 6px;
    opacity: 0;
    transition: all 0.4s cubic-bezier(0.23, 1, 0.32, 1);
    z-index: 2;

    .action-btn {
      width: 26px;
      height: 26px;
      display: flex;
      align-items: center;
      justify-content: center;
      background: rgba(255, 255, 255, 0.86);
      border-radius: var(--radius-s);
      color: var(--surface-text-secondary);
      transition: var(--transition-base);
      position: relative;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.03);

      &.shortcut {
        &.has-shortcut {
          color: var(--brand-color-pure);
          background: rgba(255, 255, 255, 0.88);
        }

        &.success::after {
          content: '';
          position: absolute;
          top: 2px;
          right: 2px;
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background: #4caf50;
          border: 1px solid #fff;
        }
      }

      &:hover {
        background: rgba(255, 255, 255, 0.96);
        color: var(--brand-color-pure);
        transform: scale(1.15);
      }

      &.delete:hover {
        background: #ff5757;
        color: #fff;
      }

      .svg_asset,
      svg {
        width: 13px;
        height: 13px;
      }
    }
  }

  &:hover {
    background: linear-gradient(180deg, rgba(255, 255, 255, 0.68) 0%, rgba(237, 244, 255, 0.58) 100%);
    border-color: rgba(var(--color-brand-1-rgb), 0.2);
    transform: translateY(-2px);
    box-shadow: 0 10px 24px rgba(var(--color-brand-2-rgb), 0.12);

    .item-actions {
      opacity: 1;
      transform: translateX(0);
    }

    .item-name {
      color: var(--surface-text-primary);
    }

    .item-meta {
      color: var(--surface-text-secondary);
    }

    .item-progress {
      .item-progress-bar {
        width: 32%;
        opacity: 0.5;
      }
    }
  }

  &.active {
    background: linear-gradient(180deg, rgba(249, 252, 255, 0.98) 0%, rgba(233, 242, 255, 0.94) 100%);
    border-color: rgba(var(--color-brand-1-rgb), 0.24);
    transform: translateY(-2px);
    box-shadow: 0 12px 28px rgba(var(--color-brand-2-rgb), 0.14);

    .active-rail {
      transform: translateY(-50%) scaleY(1);
      box-shadow: 2px 0 14px rgba(var(--color-brand-1-rgb), 0.34);
    }

    .item-actions {
      opacity: 1;
      transform: translateX(0);

      .action-btn {
        background: rgba(var(--color-brand-1-rgb), 0.12);
        color: var(--brand-color-pure);

        &:hover {
          background: var(--brand-color-pure);
          color: #fff;
        }

        &.delete:hover {
          background: #ff5757;
          color: #fff;
        }
      }
    }

    .config-input {
      color: var(--brand-color-pure);
      border-bottom-color: var(--brand-color-pure);

      &::placeholder {
        color: rgba(var(--color-brand-1-rgb), 0.4);
      }
    }

    .item-name {
      color: var(--surface-text-primary);
      font-weight: 900;
    }

    .item-meta {
      color: var(--surface-text-secondary);
    }

    .item-progress {
      .item-progress-bar {
        width: 100%;
        opacity: 1;
        box-shadow: 0 0 12px rgba(var(--color-brand-2-rgb), 0.28);
      }
    }

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 10px 24px rgba(var(--color-brand-1-rgb), 0.14);
    }
  }

  &.config-item-archive {
    opacity: 0.7;
    background: rgba(255, 255, 255, 0.25);

    &:hover {
      opacity: 1;
    }
  }
}

@media (max-width: 1560px) {
  .config-item {
    padding: 16px 14px;
  }
}
</style>
